import { deleteRange, insertChars, spliceBlocks, takeInlineTail, replaceChildren } from './ops'
import type { CoreState } from './state'
import { leaf, locate, plain, pointAt, rows, sliceInline, type Row } from './tree'

export function indentCode(
  state: CoreState, row: Row, from: number, to: number, outdent: boolean,
): void {
  const selection = state.selection
  if (!selection) return
  const entries = rows(state.blocks)
  const anchor = locate(entries, selection.anchor)
  const focus = locate(entries, selection.focus)
  const text = plain(row.block)
  const collapsed = anchor.row === focus.row && anchor.offset === focus.offset
  const starts: number[] = []
  if (collapsed && !outdent) starts.push(from)
  else {
    let at = from === 0 ? 0 : text.lastIndexOf('\n', from - 1) + 1
    do {
      starts.push(at)
      const next = text.indexOf('\n', at)
      if (next < 0) break
      at = next + 1
    } while (at < to)
  }
  for (const at of starts.toReversed()) {
    // Outdent removes a leading tab, else up to two leading spaces (BlockSuite code Shift+Tab).
    const width = outdent ? (text[at] === '\t' ? 1 : text.startsWith('  ', at) ? 2 : text[at] === ' ' ? 1 : 0) : 1
    if (outdent) {
      if (width === 0) continue
      deleteRange(state, row.block.children, at, at + width)
    } else if (!insertChars(state, row.block.children, at, '\t', 0)) {
      const tail = takeInlineTail(state, row.block.children, at)
      spliceBlocks(state, row.block.children, row.block.children.length, 0, leaf('\t'), ...tail)
    }
    for (const point of [anchor, focus]) {
      if (entries[point.row].block.id !== row.block.id) continue
      if (point.offset > at || (collapsed && point.offset === at && !outdent)) {
        point.offset = outdent ? Math.max(at, point.offset - width) : point.offset + 1
      }
    }
  }
  state.selection = {
    anchor: pointAt(entries[anchor.row], anchor.offset),
    focus: pointAt(entries[focus.row], focus.offset),
  }
}

/** Move selected logical lines as a unit, preserving the selection's columns and direction. */
export function moveCodeLines(state: CoreState, row: Row, from: number, to: number, up: boolean) {
  if (!state.selection) return
  const text = plain(row.block)
  let offset = 0
  const lines = text.split('\n').map((line) => {
    const start = offset
    offset += line.length + 1
    return { start, length: line.length }
  })
  const first = text.slice(0, from).split('\n').length - 1
  const last = text.slice(0, to).split('\n').length - 1
  if (up ? first === 0 : last === lines.length - 1) return
  const adjacent = lines[up ? first - 1 : last + 1]
  const moved = lines.splice(first, last - first + 1)
  lines.splice(up ? first - 1 : first + 1, 0, ...moved)
  const delta = (adjacent.length + 1) * (up ? -1 : 1)
  const entries = rows(state.blocks)
  const anchor = locate(entries, state.selection.anchor).offset + delta
  const focus = locate(entries, state.selection.focus).offset + delta
  const content = row.block.children
  replaceChildren(state, row.block, lines.flatMap((line, index) => {
    const result = sliceInline(content, line.start, line.start + line.length, line.start > 0, state)
    if (index < lines.length - 1) result.push(leaf('\n'))
    return result
  }))
  state.selection = { anchor: pointAt(row, anchor), focus: pointAt(row, focus) }
}
